/*
 * @Author: Raeesaa Metkari
 * @Date: 2020-04-13 22:56:48
 * @Last Modified by: Raeesaa Metkari
 * @Last Modified time: 2020-04-17 17:00:54
 */
import { CriteriaOperationType } from '@modules/models'
import { IDoorTableData } from '@services/wholesale/doorManagementService'
import { stores } from '@stores'
import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import forEach from 'lodash/forEach'
import { ObservableMap, action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import * as qs from 'query-string'
import { DoorSettingAPIService } from '../services/DoorSettings'

export class DoorManagementStore {
  @observable doorData: Array<IDoorTableData>
  @observable hasAtLeastOneNonPreBuyingBS = false
  @observable allotments = new ObservableMap()
  @observable isAllotmentsLoading = new ObservableMap()
  @observable clusterOrGroups = new ObservableMap()
  @observable isClusterOrGroupLoading = new ObservableMap()
  private doorSettingAPI = new DoorSettingAPIService()
  @observable clientIds = new Set()
  @observable buyingSessionGroupIds = new Set()

  @action
  fetchClusterOrGroups = async (channel, type) => {
    this.isClusterOrGroupLoading.set(channel + type, true)
    await this.doorSettingAPI
      .getClusterOrGroups(channel, type)
      .then(response => {
        this.clusterOrGroups.set(
          channel + type,
          response?.map(res => ({
            Id: res.id,
            Name: res.name
          }))
        )
        this.isClusterOrGroupLoading.set(channel + type, false)
      })
      .catch(() => {
        this.clusterOrGroups.set(channel + type, null)
        this.isClusterOrGroupLoading.set(channel + type, false)
      })
  }

  getClusterOrGroups = computedFn((channel, type) => {
    return this.clusterOrGroups.get(channel + type) ?? []
  })

  getClusterOrGroupsLoading = computedFn(
    (channel, type) => this.isClusterOrGroupLoading.get(channel + type) ?? false
  )

  @action
  fetchAllotments = async (buyingSessionGroupId, client, doorId, forceFetch?) => {
    this.clientIds.add(client)
    this.buyingSessionGroupIds.add(buyingSessionGroupId)
    if (!buyingSessionGroupId || !doorId) return
    if (!this.allotments.has(buyingSessionGroupId + client + doorId) || forceFetch) {
      this.isAllotmentsLoading.set(buyingSessionGroupId + client + doorId, true)
      await this.doorSettingAPI
        .getAllotmentsByCriteria({
          doorId: doorId,
          buyingSessionGroupId: buyingSessionGroupId
        })
        .then(response => {
          const data = response?.map(allot => ({
            Id: allot.id,
            Cluster: {
              Id: allot.cluster.id
            },
            ClusterGroup: {
              Id: allot.clusterGroup.id
            },
            Activity: {
              Id: allot.activity.id
            },
            Gender: {
              Id: allot.gender.id
            },
            BuyingSessionGroup: {
              Id: allot.buyingSessionGroupId
            }
          }))

          this.allotments.set(buyingSessionGroupId + client + doorId, data)
          this.isAllotmentsLoading.set(buyingSessionGroupId + client + doorId, false)
        })
        .catch(() => {
          this.allotments.set(buyingSessionGroupId + client + doorId, null)
          this.isAllotmentsLoading.set(buyingSessionGroupId + client + doorId, false)
        })
    }

    if (this.clientIds.size > 1) {
      this.removePreviousAllotments(client)
      this.clearClientIds()
    } else if (this.buyingSessionGroupIds.size > 1) {
      this.removePreviousAllotments(buyingSessionGroupId)
      this.clearBuyingSessionGroupIds()
    }
  }

  @action removePreviousAllotments(client) {
    Array.from(this.allotments.keys()).forEach(key => {
      if (!key.includes(client)) {
        this.allotments.delete(key)
      }
    })
  }

  getAllotments = computedFn((buyingSessionGroupId, client, doorId) => {
    return this.allotments.get(buyingSessionGroupId + client + doorId) ?? []
  })

  @action setAllotments = (key, data) => {
    this.isAllotmentsLoading.set(key, true)
    this.allotments.set(key, data)
    this.isAllotmentsLoading.set(key, false)
  }

  getAllotmentsLoading = computedFn(
    (buyingSessionGroupId, client, doorId) =>
      this.isAllotmentsLoading.get(buyingSessionGroupId + client + doorId) ?? false
  )

  @action clearClientIds() {
    this.clientIds.clear()
  }
  @action clearBuyingSessionGroupIds() {
    this.buyingSessionGroupIds.clear()
  }

  @action clearClusterOrGroups() {
    this.clusterOrGroups.clear()
    this.isClusterOrGroupLoading.clear()
  }

  @computed get whereInputForDoors() {
    const {
      nav: {
        queryParams: { client }
      }
    } = stores
    return {
      c8__Parent__: {
        Id: {
          operation: CriteriaOperationType.EQ,
          value: client
        }
      }
    }
  }

  @computed get queryStringForClientLink() {
    const {
      nav: {
        location: { search }
      }
    } = stores
    let qsJSON: any = cloneDeep(qs.parse(search))
    delete qsJSON.client

    return qs.stringify(qsJSON)
  }

  @action setDoorData = (doorData: Array<IDoorTableData>) => {
    const existingDoorData = this.doorData
    let expandedRecords = filter(existingDoorData, door => door.isExpanded)

    let newData = cloneDeep(doorData)
    if (expandedRecords.length) {
      // Set expanded flag to true in new data
      forEach(expandedRecords, expandedDoorRecord => {
        let expandedDoorIndex = findIndex(
          newData,
          door => door.id === expandedDoorRecord.id
        )
        if (expandedDoorIndex > -1) {
          newData[expandedDoorIndex].isExpanded = true
        }
      })
    }

    this.doorData = newData
  }

  @action onClickExpandCollapseToggle = (door: IDoorTableData) => event => {
    let doorRecordIndex = findIndex(this.doorData, doorObj => doorObj.id === door.id)

    if (doorRecordIndex > -1 && this.doorData[doorRecordIndex]) {
      let data = cloneDeep(this.doorData)
      data[doorRecordIndex].isExpanded = !this.doorData[doorRecordIndex].isExpanded
      this.doorData = data
    }
  }

  @action clearDoorData = () => {
    this.doorData = []
  }

  @action setHasAtLeastOneNonPreBuyingBS = hasAtLeastOneNonPreBuyingBS =>
    Object.assign(this, { hasAtLeastOneNonPreBuyingBS })
}

export const doorManagementStore = new DoorManagementStore()
